// libraries
import React, { useState, useRef, useEffect } from 'react'
import clsx from 'clsx'

// components
import Image from '/react4xp/components/Image'

// Fallback when XP does not pass localized copy (e.g. prototype-only pages)
const DEFAULT_VIDEO_COOKIE_CONSENT_HTML =
    'Please <a href="#" class="cookiebot-video-consent-link">accept marketing cookies</a> to view this video.'

function openCookiebotPreferences(): void {
    const C = (window as unknown as { Cookiebot?: { show?: () => void; renew?: () => void } }).Cookiebot
    if (!C) return
    if (typeof C.show === 'function') {
        C.show()
        return
    }
    if (typeof C.renew === 'function') {
        C.renew()
    }
}

function isCookiebotMarketingAllowed(): boolean {
    try {
        const c = (window as unknown as { Cookiebot?: { consent?: { marketing?: boolean } } }).Cookiebot?.consent
        return c?.marketing === true
    } catch (_e) {
        return false
    }
}

// prop types
interface VideoProps {
    className?: string
    video: {
        url: string
        playButtonColor: 'white' | 'blue' | 'red' | string
        playText: string
        playTextColor: 'white' | 'black'
        videoType: 'youtube' | 'vimeo' | 'default'
        poster?: string
        posterMedium?: string
        /** Pre-rendered HTML from XP i18n (`cookiebot.optout.msg`) */
        cookieConsentHtml?: string
    }
}

export default function Video({ video, className }: VideoProps) {

    const [isPlaying, setIsPlaying] = useState(false)
    const [currentPoster, setCurrentPoster] = useState(video.poster)
    const [showPoster, setShowPoster] = useState(true)
    const videoRef = useRef<HTMLVideoElement>(null)
    const iframeRef = useRef<HTMLIFrameElement>(null)
    const cookieOptOutRef = useRef<HTMLDivElement>(null)
    const [mute, setMute] = useState(0)
    const [marketingOverlayVisible, setMarketingOverlayVisible] = useState(true)

    const handleVideoClick = () => {

        if (video.videoType === 'default') {

            setIsPlaying(!isPlaying)
            
            if (isPlaying) {
                videoRef.current?.pause()
            } else {
                videoRef.current?.play()
            }

        } else if (video.videoType === 'youtube') {
            iframeRef.current?.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*')
            setIsPlaying(true)
        } else if (video.videoType === 'vimeo') {
            iframeRef.current?.contentWindow?.postMessage('{"method":"play"}', '*')
            setIsPlaying(true)
        }

        setShowPoster(false)
    }

    useEffect(() => {
        const handlePlayPause = () => {
            setIsPlaying(!videoRef.current?.paused)
        }

        const videoElement = videoRef.current
        videoElement?.addEventListener('play', handlePlayPause)
        videoElement?.addEventListener('pause', handlePlayPause)

        return () => {
            videoElement?.removeEventListener('play', handlePlayPause)
            videoElement?.removeEventListener('pause', handlePlayPause)
        }
    }, [])

    // change the poster based on the screen size
    useEffect(() => {
        const updatePoster = () => {
            if (window.innerWidth < 767 && video.posterMedium) {
                setCurrentPoster(video.posterMedium)
            } else {
                setCurrentPoster(video.poster)
            }
        }

        updatePoster()

        window.addEventListener('resize', updatePoster)
        return () => window.removeEventListener('resize', updatePoster)
    }, [video.poster, video.posterMedium])

    // set mute based on the screen size
    const setMuteValue = () => {
        setMute(window.innerWidth > 1023 ? 0 : 1)
    }

    useEffect(() => {
        setMuteValue()
        window.addEventListener('resize', setMuteValue)
        return () => {
            window.removeEventListener('resize', setMuteValue)
        }
    }, [])

    const isEmbed = video.videoType === 'youtube' || video.videoType === 'vimeo'

    useEffect(() => {
        if (!isEmbed) return

        function syncMarketingOverlay() {
            const allowed = isCookiebotMarketingAllowed()
            setMarketingOverlayVisible(!allowed)
        }

        syncMarketingOverlay()
        window.addEventListener('CookiebotOnConsentReady', syncMarketingOverlay)
        window.addEventListener('CookiebotOnAccept', syncMarketingOverlay)
        window.addEventListener('CookiebotOnDecline', syncMarketingOverlay)
        window.addEventListener('CookiebotOnDialogHide', syncMarketingOverlay)

        return () => {
            window.removeEventListener('CookiebotOnConsentReady', syncMarketingOverlay)
            window.removeEventListener('CookiebotOnAccept', syncMarketingOverlay)
            window.removeEventListener('CookiebotOnDecline', syncMarketingOverlay)
            window.removeEventListener('CookiebotOnDialogHide', syncMarketingOverlay)
        }
    }, [isEmbed])

    useEffect(() => {
        if (!isEmbed || typeof MutationObserver === 'undefined') return
        const iframe = iframeRef.current
        if (!iframe) return

        function syncFromIframe() {
            const allowed = isCookiebotMarketingAllowed()
            setMarketingOverlayVisible(!allowed)
        }

        const obs = new MutationObserver(syncFromIframe)
        obs.observe(iframe, { attributes: true, attributeFilter: ['src'] })
        syncFromIframe()
        return () => obs.disconnect()
    }, [isEmbed, video.videoType, video.url])

    useEffect(() => {
        if (!isEmbed || !marketingOverlayVisible) return
        const root = cookieOptOutRef.current
        if (!root) return

        function onClickCapture(e: MouseEvent) {
            const t = e.target as HTMLElement | null
            if (!t || !root.contains(t)) return
            const a = t.closest('a')
            if (!a || !root.contains(a)) return
            e.preventDefault()
            e.stopPropagation()
            openCookiebotPreferences()
        }

        root.addEventListener('click', onClickCapture, true)
        return () => root.removeEventListener('click', onClickCapture, true)
    }, [isEmbed, marketingOverlayVisible, video.cookieConsentHtml])

    // custom bg color
    const customBgColor = video.playButtonColor && !['white', 'blue', 'red'].includes(video.playButtonColor) ? { backgroundColor: video.playButtonColor } : {}

    // Normalize Vimeo input: accept full iframe HTML (paste from Vimeo), full URL, or ID / ID?h=...
    // Uses only plain strings (no URLSearchParams) for React4xp SSR (GraalJS).
    const getVimeoEmbedSrc = (input: string) => {
        let url = input.trim()
        // If editor pasted the entire iframe, extract the src attribute
        if (url.indexOf('<iframe') !== -1 && url.indexOf('src=') !== -1) {
            const srcMatch = url.match(/src\s*=\s*["']([^"']+)["']/i)
            if (srcMatch && srcMatch[1]) url = srcMatch[1]
        }
        const lastSegment = url.split('/').pop() || url
        const [idPart, ...queryParts] = lastSegment.split('?')
        const videoId = (idPart.match(/\d+/) || [idPart])[0]
        const params: Record<string, string> = { title: '0', byline: '0', portrait: '0', controls: '1' }
        const existingQuery = queryParts.join('?')
        if (existingQuery) {
            existingQuery.split('&').forEach((pair) => {
                const i = pair.indexOf('=')
                if (i !== -1) params[pair.slice(0, i)] = pair.slice(i + 1)
            })
        }
        const query = Object.keys(params).map((k) => k + '=' + encodeURIComponent(params[k])).join('&')
        return `https://player.vimeo.com/video/${videoId}?${query}`
    }

    return (
        <div
            className={clsx('video-wrapper', className)}
            onClick={handleVideoClick}
        >

            <button
                onClick={handleVideoClick}
                className='video-wrapper__button'
            ></button>

            {!isPlaying && !(isEmbed && marketingOverlayVisible) && (
                <div
                    className={clsx(
                        'video-wrapper__play-pause',
                        video.playTextColor === 'black' && 'black',
                        video.playTextColor === 'white' && 'white',

                        // bg colors
                        video.playButtonColor === 'white' && 'bg-white',
                        video.playButtonColor === 'blue' && 'bg-blue',
                        video.playButtonColor === 'red' && 'bg-red'
                    )}
                    style={customBgColor}
                >
                    {video.playText}
                </div>
            )}

            {video.videoType === 'default' && (
                <>

                    {!isPlaying && showPoster && currentPoster && (
                        <Image
                            alt='Video'
                            src={currentPoster}
                            className='cover thumb'
                        />
                    )}

                    <video
                        ref={videoRef}
                        className='cover'
                        loop
                        poster={currentPoster}
                        controls={isPlaying}
                        //preload='none'
                    >
                        <source src={video.url + '#t=0.001'} />
                    </video>

                </>
            )}

            {video.videoType === 'youtube' && (
                <>

                    <div className='youtube-video-wrapper'>
                        {!isPlaying && showPoster && currentPoster && (
                            <Image
                                alt='Video'
                                src={currentPoster}
                                className='cover thumb'
                            />
                        )}
                        {marketingOverlayVisible && (
                            <div
                                ref={cookieOptOutRef}
                                className='cookieconsent-optout-marketing'
                                role='region'
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div
                                    className='cookieconsent-optout-marketing__content'
                                >
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: video.cookieConsentHtml || DEFAULT_VIDEO_COOKIE_CONSENT_HTML,
                                        }}
                                    />
                                    <span
                                        aria-hidden='true'
                                        className='cookieconsent-optout-marketing__icon'
                                    />
                                </div>
                            </div>
                        )}
                        <iframe
                            ref={iframeRef}
                            className='cover'
                            data-cookieconsent='marketing'
                            data-src={`https://www.youtube.com/embed/${video.url}?autoplay=0&playsinline=1&mute=${mute}&loop=0&modestbranding=1&rel=0&iv_load_policy=3&title=0&controls=1&portrait=0&enablejsapi=1`}
                            title='YouTube video player'
                            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                            allowFullScreen
                            tabIndex={isPlaying ? 0 : -1}
                        ></iframe>
                    </div>
                    
                </>
            )}

            {video.videoType === 'vimeo' && (
                <>

                    <div className='youtube-video-wrapper'>
                        {!isPlaying && currentPoster && (
                            <Image
                                alt='Video'
                                src={currentPoster}
                                className='cover thumb'
                            />
                        )}
                        {marketingOverlayVisible && (
                            <div
                                ref={cookieOptOutRef}
                                className='cookieconsent-optout-marketing'
                                role='region'
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div
                                    className='cookieconsent-optout-marketing__content'
                                >
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: video.cookieConsentHtml || DEFAULT_VIDEO_COOKIE_CONSENT_HTML,
                                        }}
                                    />
                                    <span
                                        aria-hidden='true'
                                        className='cookieconsent-optout-marketing__icon'
                                    />
                                </div>
                            </div>
                        )}
                        <iframe
                            ref={iframeRef}
                            className='cover'
                            data-cookieconsent='marketing'
                            data-src={getVimeoEmbedSrc(video.url)}
                            title='Vimeo video player'
                            allow='autoplay; fullscreen; picture-in-picture'
                            allowFullScreen
                            tabIndex={isPlaying ? 0 : -1}
                        ></iframe>
                    </div>

                </>
            )}

        </div>
    )
}